import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MoreHorizontal, Pencil, Link as LinkIcon, Trash2, Lock } from 'lucide-react';
import { useSelector } from 'react-redux';

import TournamentBadge from '../TournamentBadge/TournamentBadge';

const MENU_WIDTH = 220;

const DashboardHeader = ({ onEdit, onCopyLink, onDelete, onLockStages }) => {
  const { selectedTournament } = useSelector((state) => state.tournament);
  const { selectedOrganisation } = useSelector((state) => state.organisation);

  const triggerRef = useRef(null);
  const [menu, setMenu] = useState({ isOpen: false, position: { top: 0, left: 0 } });

  const orgName =
    selectedTournament?.organisationId?.name || selectedOrganisation?.name || '';
  const venueName = selectedTournament?.matchVenue?.venue || '';
  const subtitle = venueName //[orgName, venueName].filter(Boolean).join(' • ');

  const closeMenu = () => setMenu((prev) => ({ ...prev, isOpen: false }));

  const toggleMenu = () => {
    if (menu.isOpen) {
      closeMenu();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenu({
      isOpen: true,
      position: { top: rect.bottom + 8, left: rect.right - MENU_WIDTH },
    });
  };

  useEffect(() => {
    if (!menu.isOpen) return undefined;
    const handleOutside = (e) => {
      if (
        !e.target.closest('.dashboard-header-menu-popup') &&
        !e.target.closest('.dashboard-header-menu-trigger')
      ) {
        closeMenu();
      }
    };
    const handleScroll = () => closeMenu();
    window.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [menu.isOpen]);

  const handleClick = (handler) => () => {
    closeMenu();
    handler?.();
  };

  const itemBase =
    'w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-2 transition-colors cursor-pointer';
  const itemDefault = 'text-[#FEFCEB] hover:bg-[#0D1B29]';
  const itemDestructive = 'text-red-400 hover:bg-red-500/20 hover:text-red-300';
  const divider = 'border-b border-[#192634]';

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl">
      <div className="flex items-center gap-2 min-w-0">
        <TournamentBadge
          src={selectedTournament?.logo}
          alt={`${selectedTournament?.name || 'Competition'} logo`}
          className="w-12 h-12 shrink-0 rounded-3xl object-cover border border-[#192634] bg-[#000F1F] p-2"
        />
        <div className="flex flex-col min-w-0">
          <h2 className="text-xl font-semibold text-app-text m-0 truncate">
            {selectedTournament?.name || ''}
          </h2>
          {subtitle && (
            <p className="text-app-text-secondary text-[12px] m-0 truncate">{subtitle}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <button
          ref={triggerRef}
          type="button"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menu.isOpen}
          onClick={toggleMenu}
          className="dashboard-header-menu-trigger w-10 h-10 flex items-center justify-center bg-[#082441] hover:bg-[#0a2d52] text-app-text rounded-full border border-[#192634] transition-colors"
        >
          <MoreHorizontal size={20} />
        </button>
        {onLockStages && (
          <button
            type="button"
            onClick={onLockStages}
            className="flex items-center gap-2 px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
          >
            <Lock size={14} />
            Lock stages
          </button>
        )}
      </div>

      {menu.isOpen &&
        ReactDOM.createPortal(
          <div
            role="menu"
            className="dashboard-header-menu-popup fixed z-[9999] bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl overflow-hidden"
            style={{ top: menu.position.top, left: menu.position.left, width: MENU_WIDTH }}
          >
            {onEdit && (
              <button
                role="menuitem"
                type="button"
                onClick={handleClick(onEdit)}
                className={`${itemBase} ${itemDefault} ${divider}`}
              >
                <Pencil size={16} />
                Edit competition
              </button>
            )}
            {onCopyLink && (
              <button
                role="menuitem"
                type="button"
                onClick={handleClick(onCopyLink)}
                className={`${itemBase} ${itemDefault} ${onDelete ? divider : ''}`}
              >
                <LinkIcon size={16} />
                Copy public link
              </button>
            )}
            {onDelete && (
              <button
                role="menuitem"
                type="button"
                onClick={handleClick(onDelete)}
                className={`${itemBase} ${itemDestructive}`}
              >
                <Trash2 size={16} />
                Delete competition
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default DashboardHeader;
